// css globals
import '@/styles/global.css';

// Redux
import AppConfig from '@config/AppConfig.ts';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
// Router
import Router from '@router/Router.tsx';
// Sentry Error Tracking
import * as Sentry from '@sentry/react';
import appStore, { appPersistStore } from '@store/appStore.ts';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  environment: import.meta.env.MODE, // 'development', 'staging', or 'production'
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0, // Lower sample rate in production
  // Session Replay
  replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0.5, // Higher rate in dev
  replaysOnErrorSampleRate: 1.0, // Always capture on errors
  // Additional helpful options
  beforeSend(event) {
    // In development, log to console as well
    if (import.meta.env.MODE === 'development') {
      console.error('[Sentry]', event);
    }
    return event;
  },
  // Only enabled in production by default, but can be overridden
  enabled: import.meta.env.VITE_SENTRY_DSN !== undefined && import.meta.env.VITE_SENTRY_DSN !== '',
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <HelmetProvider>
    <React.StrictMode>
      <Suspense>
        <Provider store={appStore}>
          <PersistGate loading={null} persistor={appPersistStore}>
            <MantineProvider
              defaultColorScheme={AppConfig.mantine.defaultColorScheme}
              theme={AppConfig.mantine.mantineThemeOverride}>
              <ModalsProvider>
                <Notifications />
                <RouterProvider router={Router} />
              </ModalsProvider>
            </MantineProvider>
          </PersistGate>
        </Provider>
      </Suspense>
    </React.StrictMode>
  </HelmetProvider>,
);
